import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";

export interface NoticiaDB {
  id: number;
  titulo: string;
  subtitulo: string | null;
  extracto: string | null;
  imagen: string | null;
  fecha: string | null;
  comuna: string | null;
  categoria: string | null;
  segmento: string | null;
  created_at: string;
  updated_at: string;
}

export function useNoticia(id: number | undefined) {
  const [noticia, setNoticia] = useState<NoticiaDB | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  const fetchNoticia = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from("noticias")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (err) throw err;
      if (mounted.current) {
        setNoticia(data as NoticiaDB | null);
      }
    } catch {
      if (mounted.current) {
        setError("Error cargando noticia");
      }
    } finally {
      if (mounted.current) {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    mounted.current = true;
    fetchNoticia();
    return () => {
      mounted.current = false;
    };
  }, [fetchNoticia]);

  return { noticia, loading, error };
}

export function useNoticiasRelacionadas(
  categoria: string | null,
  excludeId: number | undefined
) {
  const [noticias, setNoticias] = useState<NoticiaDB[]>([]);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  const fetchRelacionadas = useCallback(async () => {
    if (!categoria || !excludeId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from("noticias")
        .select("*")
        .eq("categoria", categoria)
        .neq("id", excludeId)
        .limit(3);
      if (err) throw err;
      if (mounted.current) {
        setNoticias((data as NoticiaDB[]) || []);
      }
    } catch {
      if (mounted.current) {
        setNoticias([]);
      }
    } finally {
      if (mounted.current) {
        setLoading(false);
      }
    }
  }, [categoria, excludeId]);

  useEffect(() => {
    mounted.current = true;
    fetchRelacionadas();
    return () => {
      mounted.current = false;
    };
  }, [fetchRelacionadas]);

  return { noticias, loading };
}